import { format } from "date-fns";

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { LoggedInUser, UsersOrderData } from "@/types";
import { Badge } from "@/components/ui/badge";
import getStatusBadgeVariant from "@/utils/getStatusBadgeVariant";
import getStatusLabel from "@/utils/getStatusLabel";

interface CustomerManagementTabProps {
	usersData: LoggedInUser[];
	ordersData: UsersOrderData[];
	isLoading: boolean;
	searchQuery: string;
}

const CustomerManagementTab = ({
	usersData,
	ordersData,
	searchQuery,
}: CustomerManagementTabProps) => {
	const getCustomerOrders = (userId: string) => {
		return ordersData.filter((order) => order.userId === userId);
	};

	const getTotalSpent = (userId: string) => {
		return ordersData
			.filter((order) => order.userId === userId && !order.cancelled)
			.reduce((total, order) => total + order.totalPrice, 0);
	};

	const getLastOrderDate = (userId: string) => {
		const customerOrders = getCustomerOrders(userId);
		if (customerOrders.length === 0) return null;
		return new Date(
			Math.max(
				...customerOrders.map((order) => new Date(order.createdAt).getTime()),
			),
		);
	};

	const filteredUsers = usersData.filter((user) => {
		if (!searchQuery) return true;
		const searchLower = searchQuery.toLowerCase();
		return (
			user.fullName?.toLowerCase().includes(searchLower) ||
			user.email?.toLowerCase().includes(searchLower) ||
			user.phoneNumber?.toLowerCase().includes(searchLower) ||
			getTotalSpent(user.id).toString().includes(searchLower)
		);
	});

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>Customers</CardTitle>
					<CardDescription>
						{searchQuery
							? `Searching for "${searchQuery}" in customers`
							: "Customer information and order history"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{filteredUsers.map((user) => {
							const customerOrders = getCustomerOrders(user.id);
							const totalSpent = getTotalSpent(user.id);
							const lastOrderDate = getLastOrderDate(user.id);

							return (
								<div key={user.id} className="rounded-lg border p-4">
									<div className="flex items-center justify-between">
										<div>
											<h3 className="text-lg font-medium">{user.fullName}</h3>
											<p className="text-sm text-muted-foreground">
												{user.email}
											</p>
											{user.phoneNumber && (
												<p className="text-sm text-muted-foreground">
													{user.phoneNumber}
												</p>
											)}
										</div>
										<div className="text-right">
											<p className="font-medium">£{totalSpent.toFixed(2)}</p>
											<p className="text-sm text-muted-foreground">
												{customerOrders.length} orders
											</p>
											{lastOrderDate && (
												<p className="text-sm text-muted-foreground">
													Last order: {format(lastOrderDate, "PPP")}
												</p>
											)}
										</div>
									</div>

									<div className="mt-4">
										<h4 className="mb-2 font-medium">Recent Orders</h4>
										<div className="space-y-2">
											{customerOrders.slice(0, 3).map((order) => (
												<div
													key={order.id}
													className="flex items-center justify-between rounded-md bg-muted p-2"
												>
													<div>
														<p className="text-sm">
															{format(new Date(order.collectionDate), "PPP")}
														</p>
														<p className="text-xs text-muted-foreground">
															{order.basketItems.length} items
														</p>
													</div>
													<div className="text-right">
														<Badge
															className={`text-xs text-white ${getStatusBadgeVariant(
																order,
															)}`}
														>
															{getStatusLabel(order)}
														</Badge>
														<p className="mt-1 text-sm font-medium">
															£{order.totalPrice.toFixed(2)}
														</p>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</CardContent>
			</Card>
		</div>
	);
};

export default CustomerManagementTab;
